import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { Mail, Lock, AlertCircle, Loader, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '../../services/authService';
import GoogleLogo from '@/assets/google.svg';
import { loginFormSchema, tutorProfileSchema, parentProfileSchema, passwordSchema } from '@/schemas/authSchemas';
import { getErrorMessage, isApiException } from '@/api/errorHandler';
import { profilesApi } from '@/api/services/profilesApi';
import { authApi } from '@/api/services/authApi';
import { saveTokens, getPendingRegistration, clearPendingRegistration, clearTokens } from '@/utils/storage';
import { auth } from '@/firebase';
import type { User } from 'firebase/auth';
import type { BackendUser, ProfileData } from '@/types/api';
import { GENDER_OPTIONS, NATIONALITIES } from '@/constants/dropdownOptions';
import { useAuthContext } from '@/contexts';
import { useModal } from '@/contexts';
import type { LoginModalOptions } from '@/contexts/ModalContext';
import PhoneNumberInput from '../common/PhoneNumberInput';
import SearchableInput from '../common/SearchableInput';
import SearchableDropdown from '../common/SearchableDropdown';
import { BirthdayPicker } from '@tutor/ui';
import { logger } from '@/utils/logger';
import { captureError, pushBreadcrumb } from '@/debug/debugStore';
import { useNavigate } from 'react-router-dom';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  presentation?: 'modal' | 'page';
  showPageBackButton?: boolean;
  onBackToRegistration?: () => void;
  onRegistrationResolved?: (resolution: {
    type: 'music_tutor_profile';
    status: 'approved' | 'pending';
  }) => void;
  options?: LoginModalOptions;
}

type UserType = 'parent' | 'tutor';
type AuthMode = 'login' | 'signup';

type ModalView =
  | 'login'
  | 'signup_method'
  | 'signup_email'
  | 'signup_password'
  | 'verify_otp'           // After signup password or failed login
  | 'signup_personalInfo'
  | 'forgot_password_email'
  | 'forgot_password_otp'
  | 'forgot_password_reset';

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  presentation = 'modal',
  showPageBackButton = true,
  onBackToRegistration,
  onRegistrationResolved,
  options,
}) => {
  const navigate = useNavigate();
  const { updateFirebaseUser, updateBackendUser, updateProfile } = useAuthContext();
  const { openTutorRegistration, openParentRegistration, openMusicTutorRegistration, openMusicParentRegistration, openCloseWarning, setOnCloseWarningConfirm, loginModalOptions } = useModal();
  const activeLoginOptions = options ?? loginModalOptions;
  const [userType, setUserType] = useState<UserType>('parent');
  const [authMode, setAuthMode] = useState<AuthMode>('login');

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formModifiedAfterError, setFormModifiedAfterError] = useState(true);

  // Post-registration state
  const [isPostRegistration, setIsPostRegistration] = useState(false);
  // Locked role — set when opened from a pending registration flow
  const [isRoleLocked, setIsRoleLocked] = useState(false);

  // Deferred auth state: hold Firebase/backend user until profile creation completes,
  // so NavBar never shows the user as logged in before they have a profile.
  const pendingAuthRef = useRef<{
    firebaseUser: User | null;
    backendUser: BackendUser;
    userRole: UserType;
  } | null>(null);

  // Modal view state - replaces signupStep
  const [modalView, setModalView] = useState<ModalView>('login');

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Personal info for both roles
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [nationality, setNationality] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');

  // When the modal opens and there is a pending registration, pre-select
  // the matching user type and jump straight to the sign-up flow.
  useEffect(() => {
    logger.log('LoginModal', 'useEffect triggered - isOpen changed', isOpen);
    if (!isOpen) {
      logger.log('LoginModal', 'Modal closed, returning early');
      return;
    }

    const pending = getPendingRegistration();
    logger.log('LoginModal', 'Pending registration', pending);

    const shouldCheckPending = pending && !activeLoginOptions?.ignorePending;

    if (shouldCheckPending) {
      logger.log('LoginModal', 'Found pending registration - setting up signup flow');
      setUserType(pending.type.includes('tutor') ? 'tutor' : 'parent');
      setAuthMode('signup');
      setModalView('signup_method');
      setIsRoleLocked(true);
    } else {
      logger.log('LoginModal', 'No pending registration (or ignored) - checking options');
      // Apply options if any, otherwise default to login
      setIsRoleLocked(activeLoginOptions?.fromRegistration || false);

      if (activeLoginOptions?.initialView) {
        if (activeLoginOptions.initialUserType) setUserType(activeLoginOptions.initialUserType);
        setModalView(activeLoginOptions.initialView);
      } else if (activeLoginOptions?.initialMode === 'signup') {
        setAuthMode('signup');
        setModalView('signup_method');
      } else {
        setAuthMode('login');
        setModalView('login');
      }
    }
  }, [activeLoginOptions, isOpen]);

  // Auto-clear error messages after 5 seconds and scroll to top
  useEffect(() => {
    if (error) {
      // Disable form submission until user modifies the form
      setFormModifiedAfterError(false);

      // Scroll modal content to top to show error
      const modalContent = document.querySelector('.modal-content');
      if (modalContent) {
        modalContent.scrollTo({ top: 0, behavior: 'smooth' });
      }

      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Listen for dev panel auto-fill event
  useEffect(() => {
    const handleAutoFill = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { email, password } = customEvent.detail;
      setEmail(email);
      setPassword(password);
      handleFormChange();
    };

    if (import.meta.env.DEV) {
      window.addEventListener('devPanel:autoFill', handleAutoFill);
      return () => window.removeEventListener('devPanel:autoFill', handleAutoFill);
    }
  }, []);

  // Helper function to mark form as modified and clear error
  const handleFormChange = () => {
    if (!formModifiedAfterError) {
      setFormModifiedAfterError(true);
    }
  };

  // Helper function to reset form state
  const resetFormState = () => {
    logger.log('LoginModal', 'resetFormState executing');
    logger.log('LoginModal', 'Resetting', { modalView, isPostRegistration, userType });
    setModalView('login');
    setFullName('');
    setPhone('');
    setNationality('');
    setDateOfBirth('');
    setGender('');
    setEmail('');
    setPassword('');
    setNewPassword('');
    setOtp('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
    setFormModifiedAfterError(true);
    // Preserve role lock when coming from a registration flow (e.g. AI matching)
    // so switching from signup → login doesn't expose the role toggle
    setIsRoleLocked(activeLoginOptions?.fromRegistration || false);
    pendingAuthRef.current = null;
    logger.log('LoginModal', 'resetFormState completed');
  };

  // Helper function to render auth divider
  const renderAuthDivider = (text: string) => (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-300"></div>
      </div>
      <div className="relative flex justify-center text-sm">
        <span className="px-2 bg-white text-gray-500">{text}</span>
      </div>
    </div>
  );

  // Helper function to render password input field
  const renderPasswordInput = (
    label: string,
    value: string,
    onChange: (val: string) => void
  ) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="password"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            handleFormChange();
          }}
          placeholder="Enter password"
          className={`input-field pl-10 w-full ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          disabled={loading}
        />
      </div>
    </div>
  );

  // Error message component
  const renderErrorMessage = () => {
    if (!error) return null;
    return (
      <div className="flex gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  };

  // Success message component
  const renderSuccessMessage = () => {
    if (!success) return null;
    return (
      <div className="flex gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div className="h-5 w-5 text-green-500 flex-shrink-0 flex items-center justify-center">✓</div>
        <p className="text-sm text-green-700">{success}</p>
      </div>
    );
  };

  const saveProfileToSession = (
    role: UserType,
    profileData?: ProfileData | null
  ) => {
    if (!profileData) return;

    const profileWithRole: ProfileData = {
      ...profileData,
      role,
    };

    updateProfile(profileWithRole);
    localStorage.setItem('client_userProfile', JSON.stringify(profileWithRole));
  };

  const commitAuthenticatedState = (
    role: UserType,
    backendUser: BackendUser,
    profileData?: ProfileData | null
  ) => {
    localStorage.setItem('client_userRole', role);
    updateFirebaseUser(auth.currentUser);
    updateBackendUser(backendUser);
    pendingAuthRef.current = null;
    saveProfileToSession(role, profileData);
  };

  const refreshSessionTokens = async () => {
    try {
      const { jwtToken, refreshToken } = await authApi.refreshAccessToken();
      saveTokens(jwtToken, refreshToken);
    } catch (refreshError) {
      logger.warn('LoginModal', 'Token refresh failed (non-critical)', refreshError);
    }
  };

  const resumeMusicRegistration = (type: 'music_tutor' | 'music_parent') => {
    if (onBackToRegistration) {
      onBackToRegistration();
      return;
    }

    const pending = getPendingRegistration();
    if (pending?.type === type && pending.presentation === 'page') {
      navigate(type === 'music_tutor' ? '/music/register/tutor' : '/music/register/parent');
      return;
    }

    if (type === 'music_tutor') openMusicTutorRegistration();
    else openMusicParentRegistration();
  };

  const returnToRegistration = () => {
    if (onBackToRegistration) {
      onBackToRegistration();
      return;
    }

    onClose();
    if (activeLoginOptions?.registrationService === 'music') {
      if (userType === 'tutor') resumeMusicRegistration('music_tutor');
      else resumeMusicRegistration('music_parent');
    } else if (userType === 'tutor') {
      openTutorRegistration();
    } else {
      openParentRegistration();
    }
  };

  const consumePendingRegistration = (
    role: UserType,
    hasSelectedRoleProfile: boolean,
    selectedProfile?: ProfileData | null
  ) => {
    const pending = getPendingRegistration();
    const controlledMusicType = onBackToRegistration && activeLoginOptions?.registrationService === 'music'
      ? role === 'tutor' ? 'music_tutor' : 'music_parent'
      : null;
    const registrationType = controlledMusicType ?? pending?.type;
    const pendingRole = registrationType?.includes('tutor') ? 'tutor' : registrationType?.includes('parent') ? 'parent' : null;

    if (!registrationType || pendingRole !== role) {
      return;
    }

    if (registrationType === 'music_tutor') {
      const musicStatus = selectedProfile?.musicProfile?.verificationStatus;
      if (musicStatus === 'approved' || musicStatus === 'pending') {
        clearPendingRegistration();
        if (onRegistrationResolved) {
          onRegistrationResolved({ type: 'music_tutor_profile', status: musicStatus });
          return;
        }
        toast.info(
          musicStatus === 'approved'
            ? 'This account already has an approved Music Tutor profile.'
            : 'This Music Tutor profile is already pending review.'
        );
        navigate('/music');
        return;
      }
      resumeMusicRegistration('music_tutor');
      return;
    }

    if (registrationType === 'music_parent') {
      resumeMusicRegistration('music_parent');
      return;
    }

    if (hasSelectedRoleProfile) {
      clearPendingRegistration();

      if (role === 'tutor') {
        toast.error('This account already has a tutor profile.');
      }

      return;
    }

    if (role === 'tutor') {
      openTutorRegistration();
      return;
    }

    openParentRegistration();
  };

  const recoverExistingTutorProfile = async (backendUser: BackendUser) => {
    commitAuthenticatedState('tutor', backendUser);

    const existingProfile = await profilesApi.getProfile('tutor');
    saveProfileToSession('tutor', existingProfile);
    await refreshSessionTokens();
    resetFormState();
    setIsPostRegistration(false);
    onClose();

    if (activeLoginOptions?.registrationService === 'music') {
      consumePendingRegistration('tutor', true, existingProfile);
      return;
    }

    clearPendingRegistration();
    toast.error('This account already has a tutor profile.');
  };

  const handleEmailAuth = () => {
    setError(null);
    setModalView('signup_email');
    setFormModifiedAfterError(true);
  };

  const handleSendVerificationEmail = async () => {
    setLoading(true);
    setError(null);
    try {
      // Validate email format
      const emailValidation = loginFormSchema.pick({ email: true }).parse({ email });
      setEmail(emailValidation.email);
      setModalView('signup_password');
      setFormModifiedAfterError(true);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const passwordValidation = passwordSchema.parse(password);

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Create Firebase user and authenticate with backend
      // Backend will send OTP automatically
      await authService.signUpWithEmail(email, passwordValidation, userType);

      logger.log('LoginModal', 'Firebase user created, OTP sent to', email);

      // Move to OTP verification step
      setModalView('verify_otp');
      setFormModifiedAfterError(true);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      logger.error('LoginModal', 'Password submit error', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonalInfoSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      // Validate and call appropriate profile creation API based on user type
      let profileResult;

      if (userType === 'tutor') {
        // Validate tutor profile data
        const tutorData = tutorProfileSchema.parse({
          fullName,
          phone,
          nationality,
          dateOfBirth,
          gender,
        });
        profileResult = await profilesApi.createTutorProfile({
          ...tutorData,
          serviceType: activeLoginOptions?.registrationService === 'music' ? 'music' : 'academic',
        });
      } else {
        // Validate parent profile data (no dateOfBirth, but gender is required)
        const parentData = {
          fullName,
          phone,
          nationality,
          gender,
        };

        const validatedParentData = parentProfileSchema.parse(parentData);
        profileResult = await profilesApi.createParentProfile(validatedParentData);
      }

      logger.log('LoginModal', `${userType} profile created successfully`, {
        profileId: profileResult.id,
        fullName: profileResult.fullName,
      });

      // Apply deferred auth state now that the profile is confirmed —
      // this is the first moment NavBar should show the user as logged in
      if (pendingAuthRef.current) {
        commitAuthenticatedState(
          pendingAuthRef.current.userRole,
          pendingAuthRef.current.backendUser
        );
      }

      // Save profile to context and localStorage with role field
      saveProfileToSession(userType, profileResult);

      // Refresh token to include new profile IDs (tutorId/parentId)
      await refreshSessionTokens();

      // Reset and close modal on success; re-open registration if pending
      pushBreadcrumb({ category: 'user', message: `Profile created (${userType})` });
      resetFormState();
      setIsPostRegistration(false);

      onClose();

      // For tutors, resume the originating registration presentation after profile creation
      if (userType === 'tutor') {
        if (activeLoginOptions?.registrationService === 'music') resumeMusicRegistration('music_tutor');
        else openTutorRegistration();
      } else {
        // For parents, only open if there's a pending registration
        const pending = getPendingRegistration();
        if (pending?.type === 'music_parent') resumeMusicRegistration('music_parent');
        else if (pending?.type === 'parent') openParentRegistration();
      }
    } catch (err) {
      if (
        userType === 'tutor' &&
        isApiException(err) &&
        err.code === 'PROFILE_ALREADY_EXISTS' &&
        pendingAuthRef.current
      ) {
        try {
          await recoverExistingTutorProfile(pendingAuthRef.current.backendUser);
          return;
        } catch (recoveryError) {
          logger.error('LoginModal', 'Failed to recover existing tutor profile after duplicate create', recoveryError);
        }
      }

      const errorMessage = getErrorMessage(err);
      logger.error('LoginModal', `${userType} profile creation error`, err);
      captureError('LoginModal:profileCreate', err);
      pushBreadcrumb({ category: 'user', message: `Profile creation failed (${userType})` });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const validatedData = loginFormSchema.parse({ email, password });

      // Sign in with Firebase (creates user in Firebase if doesn't exist)
      const firebaseUser = await authService.signInWithEmail(validatedData.email, validatedData.password, userType);

      // Check if email is verified in backend
      if (!firebaseUser.user.emailVerified) {
        // Email not verified - show OTP verification view
        logger.log('LoginModal', 'Email not verified, showing OTP verification');
        setAuthMode('signup'); // Treat as signup flow
        setModalView('verify_otp');
        setFormModifiedAfterError(true);
        setLoading(false);
        return;
      }

      logger.log('LoginModal', 'Email login successful');
      pushBreadcrumb({ category: 'user', message: `Email login success (${userType})` });

      const hasSelectedRoleProfile = userType === 'tutor'
        ? firebaseUser.user.hasTutorProfile
        : firebaseUser.user.hasParentProfile;

      if (!hasSelectedRoleProfile) {
        // Defer auth state — NavBar should not show user as logged in until they have a profile
        pendingAuthRef.current = { firebaseUser: auth.currentUser, backendUser: firebaseUser.user, userRole: userType };
        logger.log('LoginModal', 'No profile for selected role - deferring auth state in handleEmailLogin');
        setIsPostRegistration(true);
        setAuthMode('signup');
        setModalView('signup_personalInfo');
        setFormModifiedAfterError(true);
      } else {
        // Profile exists — commit auth state immediately
        commitAuthenticatedState(
          userType,
          firebaseUser.user,
          userType === 'tutor' ? firebaseUser.tutorProfile : firebaseUser.parentProfile
        );

        onClose();
        resetFormState();
        consumePendingRegistration(
          userType,
          true,
          userType === 'tutor' ? firebaseUser.tutorProfile : firebaseUser.parentProfile
        );
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      logger.error('LoginModal', 'Email login error', err);
      captureError('LoginModal:emailLogin', err);
      pushBreadcrumb({ category: 'user', message: `Email login failed (${userType})` });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError(null);

    let authSettled = false;

    // Detect when user closes the Google popup without completing auth
    const onWindowFocus = () => {
      setTimeout(() => {
        if (!authSettled) {
          authSettled = true;
          setLoading(false);
        }
      }, 500);
    };
    window.addEventListener('focus', onWindowFocus, { once: true });

    try {
      // Sign in with Google + Backend API
      const result = await authService.signInWithGoogle(userType);
      authSettled = true;
      window.removeEventListener('focus', onWindowFocus);

      logger.log('LoginModal', 'Google auth successful', {
        userId: result.user.userId,
        email: result.user.email,
        hasTutorProfile: result.user.hasTutorProfile,
        hasParentProfile: result.user.hasParentProfile,
      });
      pushBreadcrumb({ category: 'user', message: `Google auth success (${userType})` });

      // Check if email is verified
      if (!result.user.emailVerified) {
        // Defer auth state — email not yet verified
        pendingAuthRef.current = { firebaseUser: auth.currentUser, backendUser: result.user, userRole: userType };
        logger.log('LoginModal', 'Email not verified, showing OTP verification');
        setAuthMode('signup');
        setModalView('verify_otp');
        setFormModifiedAfterError(true);
        setLoading(false);
        return;
      }

      // Check if selected role is registered
      const hasSelectedRoleProfile = userType === 'tutor'
        ? result.user.hasTutorProfile
        : result.user.hasParentProfile;

      if (!hasSelectedRoleProfile) {
        // Defer auth state — NavBar should not show user as logged in until they have a profile
        pendingAuthRef.current = { firebaseUser: auth.currentUser, backendUser: result.user, userRole: userType };
        logger.log('LoginModal', 'No profile for selected role - deferring auth state in handleGoogleAuth');
        setIsPostRegistration(true);
        setAuthMode('signup');
        setModalView('signup_personalInfo');
        setFormModifiedAfterError(true);
      } else {
        // Profile exists — commit auth state immediately
        commitAuthenticatedState(
          userType,
          result.user,
          userType === 'tutor' ? result.tutorProfile : result.parentProfile
        );

        onClose();
        resetFormState();
        consumePendingRegistration(
          userType,
          true,
          userType === 'tutor' ? result.tutorProfile : result.parentProfile
        );
      }
    } catch (err) {
      authSettled = true;
      window.removeEventListener('focus', onWindowFocus);
      const errorMessage = getErrorMessage(err);
      logger.error('LoginModal', 'Google auth error', err);
      captureError('LoginModal:googleAuth', err);
      pushBreadcrumb({ category: 'user', message: `Google auth failed (${userType})` });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Validate email format
      const emailValidation = loginFormSchema.pick({ email: true }).parse({ email });

      // Send password reset OTP
      await authService.sendPasswordReset(emailValidation.email);

      logger.log('LoginModal', 'Password reset OTP sent to', emailValidation.email);

      // Move to forgot password OTP step
      setModalView('forgot_password_otp');
      setFormModifiedAfterError(true);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      logger.error('LoginModal', 'Password reset error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!otp) {
        throw new Error('Verification code is required');
      }

      const result = await authService.verifyEmailOTP(email, otp);

      logger.log('LoginModal', 'Email verified successfully');

      // Move to personal info if in signup flow, otherwise check profile
      if (authMode === 'signup') {
        // New signup — definitely no profile yet, defer auth state
        pendingAuthRef.current = { firebaseUser: auth.currentUser, backendUser: result.user, userRole: userType };
        logger.log('LoginModal', 'OTP verified in signup flow - deferring auth state until profile is created');
        setIsPostRegistration(true);
        setModalView('signup_personalInfo');
      } else {
        // Login flow - check if profile exists
        const hasSelectedRoleProfile = userType === 'tutor'
          ? result.user.hasTutorProfile
          : result.user.hasParentProfile;

        if (!hasSelectedRoleProfile) {
          // Defer auth state — no profile yet
          pendingAuthRef.current = { firebaseUser: auth.currentUser, backendUser: result.user, userRole: userType };
          logger.log('LoginModal', 'No profile for selected role - deferring auth state in handleVerifyOtp');
          setIsPostRegistration(true);
          setAuthMode('signup');
          setModalView('signup_personalInfo');
          setFormModifiedAfterError(true);
        } else {
          // Profile exists — commit auth state immediately
          commitAuthenticatedState(
            userType,
            result.user,
            userType === 'tutor' ? result.tutorProfile : result.parentProfile
          );

          onClose();
          resetFormState();
          consumePendingRegistration(
            userType,
            true,
            userType === 'tutor' ? result.tutorProfile : result.parentProfile
          );
        }
      }

      setFormModifiedAfterError(true);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      logger.error('LoginModal', 'OTP verification error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError(null);
    try {
      await authService.resendVerificationOTP(email);
      setSuccess('A new verification code has been sent to your email');
      setOtp('');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      logger.error('LoginModal', 'Resend OTP error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordOtpSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!otp) {
        throw new Error('Reset code is required');
      }
      // Move to password reset step - OTP validation happens on submission
      setModalView('forgot_password_reset');
      setFormModifiedAfterError(true);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetWithOtp = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!newPassword) {
        throw new Error('New password is required');
      }
      if (newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }
      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      await authService.resetPasswordWithOTP(email, otp, newPassword);

      setSuccess('Password reset successfully! You can now log in with your new password.');
      
      setTimeout(() => {
        // Reset to login with pre-filled email
        resetFormState();
        setModalView('login');
        setEmail(email);
        setPassword('');
        setSuccess(null);
      }, 2000);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      logger.error('LoginModal', 'Password reset error', err);
    } finally {
      setLoading(false);
    }
  };


  // ==================== RENDER OTP INPUT ====================

  const renderOtpInput = () => (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 text-center">Enter 6-digit code</label>
      <div className="flex gap-2 justify-center">
        {Array(6)
          .fill(0)
          .map((_, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={otp[index] || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  const newOtp = otp.split('');
                  newOtp[index] = value;
                  setOtp(newOtp.join(''));
                  handleFormChange();

                  // Auto-focus next input
                  if (value && index < 5) {
                    const nextInput = document.getElementById(`otp-input-${index + 1}`);
                    nextInput?.focus();
                  }
                }
              }}
              onKeyDown={(e) => {
                // Handle backspace
                if (e.key === 'Backspace' && !otp[index] && index > 0) {
                  const prevInput = document.getElementById(`otp-input-${index - 1}`);
                  prevInput?.focus();
                }
              }}
              id={`otp-input-${index}`}
              className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none disabled:opacity-50"
              disabled={loading}
            />
          ))}
      </div>
    </div>
  );

  // ==================== RENDER FUNCTIONS ====================

  const renderSignupMethod = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      <button
        onClick={handleGoogleAuth}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin" /> : <img src={GoogleLogo} alt="Google" className="h-5 w-5" />}
        <span className="font-medium text-gray-700">Continue with Google</span>
      </button>

      {renderAuthDivider('or')}

      <button
        onClick={handleEmailAuth}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Mail className="h-5 w-5 text-blue-500" />
        <span className="font-medium text-gray-700">Sign up with Email</span>
      </button>

      {!(presentation === 'page' && onBackToRegistration) && (
        <button
          type="button"
          onClick={() => {
            if (activeLoginOptions?.fromRegistration) {
              returnToRegistration();
            } else {
              setModalView('login');
              setAuthMode('login');
            }
          }}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4" />
          {activeLoginOptions?.fromRegistration ? 'Back to Form' : 'Back to Login'}
        </button>
      )}
    </div>
  );

  const renderSignupEmail = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            handleFormChange();
          }}
          placeholder="your.email@example.com"
          className={`input-field pl-10 w-full ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          disabled={loading}
        />
      </div>

      <button
        onClick={handleSendVerificationEmail}
        disabled={!email || loading || !formModifiedAfterError}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        Next
      </button>

      <button
        type="button"
        onClick={() => setModalView('signup_method')}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    </div>
  );

  const renderSignupPassword = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      {renderPasswordInput('Choose your own password', password, setPassword)}
      {renderPasswordInput('Confirm Password', confirmPassword, setConfirmPassword)}

      <button
        onClick={handlePasswordSubmit}
        disabled={!password || !confirmPassword || password !== confirmPassword || loading || !formModifiedAfterError}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        Create Account
      </button>

      <button
        type="button"
        onClick={() => setModalView('signup_email')}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    </div>
  );

  const renderVerifyOtp = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      {renderSuccessMessage()}
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          We've sent a 6-digit verification code to <strong>{email}</strong>
        </p>
      </div>

      {renderOtpInput()}

      <button
        onClick={handleVerifyOtp}
        disabled={otp.length !== 6 || loading || !formModifiedAfterError}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        Verify Code
      </button>

      <button
        onClick={handleResendOtp}
        disabled={loading}
        className="w-full text-center text-xs text-primary hover:text-primary/80 disabled:opacity-50"
      >
        Didn't receive the code? Resend
      </button>

      {authMode === 'signup' && (
        <button
          type="button"
          onClick={() => setModalView('signup_password')}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}
    </div>
  );

  const renderSignupPersonalInfo = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-2">Personal Information</h3>
        <p className="text-sm text-gray-600">
          {isPostRegistration ? 'Complete your profile information' : 'Enter your personal details to continue'}
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Full Name <span className="text-red-500">*</span></label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            handleFormChange();
          }}
          placeholder="Enter your full name"
          className={`input-field w-full ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Phone Number <span className="text-red-500">*</span></label>
        <PhoneNumberInput
          value={phone}
          onChange={(val) => {
            setPhone(val);
            handleFormChange();
          }}
          placeholder="Enter phone number"
          required={true}
          disabled={loading}
          defaultCountry="MY"
        />
      </div>

      {userType === 'tutor' && (
        <>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Date of Birth <span className="text-red-500">*</span></label>
            <BirthdayPicker
              value={dateOfBirth}
              onChange={(val) => { setDateOfBirth(val); handleFormChange(); }}
              disabled={loading}
              error={!!error}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Gender <span className="text-red-500">*</span></label>
            <SearchableDropdown
              options={GENDER_OPTIONS}
              value={gender}
              onChange={(val) => {
                setGender(String(val));
                handleFormChange();
              }}
              placeholder="Select Gender"
              searchable={false}
              disabled={loading}
            />
          </div>
        </>
      )}

      {userType === 'parent' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Gender <span className="text-red-500">*</span></label>
          <SearchableDropdown
            options={GENDER_OPTIONS}
            value={gender}
            onChange={(val) => {
              setGender(String(val));
              handleFormChange();
            }}
            placeholder="Select Gender"
            searchable={false}
            disabled={loading}
          />
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Nationality <span className="text-red-500">*</span></label>
        <SearchableInput
          options={NATIONALITIES}
          value={nationality}
          onChange={(val) => {
            setNationality(val);
            handleFormChange();
          }}
          placeholder="Search and select nationality"
          strictSelection={true}
          disabled={loading}
        />
      </div>

      <button
        onClick={handlePersonalInfoSubmit}
        disabled={
          !fullName || !phone || !nationality || !gender || loading ||
          (userType === 'tutor' && !dateOfBirth) || !formModifiedAfterError
        }
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        {isPostRegistration ? 'Complete Profile' : 'Complete Sign Up'}
      </button>

      {!isPostRegistration && (
        <button
          type="button"
          onClick={() => {
            logger.log('LoginModal', 'Back button clicked from signup_personalInfo');
            logger.log('LoginModal', 'Current state before navigation', {
              modalView,
              userType,
              email,
              fullName,
              loading,
              isPostRegistration
            });
            logger.log('LoginModal', 'Navigation: signup_personalInfo to verify_otp');
            setModalView('verify_otp');
          }}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}
    </div>
  );

  const renderLogin = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      <button
        type="button"
        onClick={handleGoogleAuth}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin" /> : <img src={GoogleLogo} alt="Google" className="h-5 w-5" />}
        <span className="font-medium text-gray-700">Continue with Google</span>
      </button>

      {renderAuthDivider('or login with email')}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Email</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              handleFormChange();
            }}
            placeholder="your.email@example.com"
            className={`input-field pl-10 w-full ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            disabled={loading}
          />
        </div>
      </div>

      {renderPasswordInput('Password', password, setPassword)}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            setModalView('forgot_password_email');
            setError(null);
            setFormModifiedAfterError(true);
          }}
          disabled={loading}
          className="text-sm text-primary hover:text-primary/80 disabled:opacity-50"
        >
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={!email || !password || loading || !formModifiedAfterError}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        Log In
      </button>
    </div>
  );

  const renderForgotPasswordEmail = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Enter your email address and we'll send you a code to reset your password.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Email Address</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              handleFormChange();
            }}
            placeholder="your.email@example.com"
            className={`input-field pl-10 w-full ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            disabled={loading}
          />
        </div>
      </div>

      <button
        onClick={handlePasswordResetSubmit}
        disabled={!email || loading || !formModifiedAfterError}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        Send Reset Code
      </button>

      <button
        type="button"
        onClick={() => {
          setModalView('login');
          setError(null);
          setSuccess(null);
          setEmail('');
          setOtp('');
          setFormModifiedAfterError(true);
        }}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Login
      </button>
    </div>
  );

  const renderForgotPasswordOtp = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      {renderSuccessMessage()}
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Enter the 6-digit code we sent to <strong>{email}</strong>
        </p>
      </div>

      {renderOtpInput()}

      <button
        onClick={handleForgotPasswordOtpSubmit}
        disabled={otp.length !== 6 || loading || !formModifiedAfterError}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        Verify Code
      </button>

      <button
        onClick={handleResendOtp}
        disabled={loading}
        className="w-full text-center text-xs text-primary hover:text-primary/80 disabled:opacity-50"
      >
        Didn't receive the code? Resend
      </button>

      <button
        type="button"
        onClick={() => {
          setModalView('forgot_password_email');
          setError(null);
          setOtp('');
          setFormModifiedAfterError(true);
        }}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    </div>
  );

  const renderForgotPasswordReset = () => (
    <div className="space-y-6">
      {renderErrorMessage()}
      {renderSuccessMessage()}
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Enter your new password below.
        </p>
      </div>

      {renderPasswordInput('New Password', newPassword, setNewPassword)}
      {renderPasswordInput('Confirm New Password', confirmPassword, setConfirmPassword)}

      <button
        onClick={handlePasswordResetWithOtp}
        disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword || loading || !formModifiedAfterError}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader className="h-5 w-5 animate-spin inline mr-2" /> : null}
        Reset Password
      </button>

      <button
        type="button"
        onClick={() => {
          setModalView('forgot_password_otp');
          setError(null);
          setNewPassword('');
          setConfirmPassword('');
          setFormModifiedAfterError(true);
        }}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    </div>
  );

  const renderContent = () => {
    switch (modalView) {
      case 'login':
        return renderLogin();
      case 'signup_method':
        return renderSignupMethod();
      case 'signup_email':
        return renderSignupEmail();
      case 'signup_password':
        return renderSignupPassword();
      case 'verify_otp':
        return renderVerifyOtp();
      case 'signup_personalInfo':
        return renderSignupPersonalInfo();
      case 'forgot_password_email':
        return renderForgotPasswordEmail();
      case 'forgot_password_otp':
        return renderForgotPasswordOtp();
      case 'forgot_password_reset':
        return renderForgotPasswordReset();
      default:
        return renderLogin();
    }
  };

  const getModalTitle = (): string => {
    switch (modalView) {
      case 'login':
        return 'Log In';
      case 'signup_method':
      case 'signup_email':
      case 'signup_password':
      case 'verify_otp':
      case 'signup_personalInfo':
        return 'Sign Up';
      case 'forgot_password_email':
      case 'forgot_password_otp':
      case 'forgot_password_reset':
        return 'Reset Password';
      default:
        return 'Log In';
    }
  };

  const handleClose = () => {
    logger.log('LoginModal', 'Modal close triggered');
    logger.log('LoginModal', 'Close context', {
      currentModalView: modalView,
      isPostRegistration,
      userType,
      loading
    });
    
    // Show warning if closing profile registration form
    if (modalView === 'signup_personalInfo' && isPostRegistration) {
      logger.log('LoginModal', 'Showing close confirmation warning for post-registration');
      
      // Set the callback that will be executed when user confirms logout
      const confirmLogoutCallback = () => {
        logger.log('LoginModal', 'User confirmed logout - clearing tokens and closing modal');

        logger.log('LoginModal', 'Closing post-registration profile form - clearing tokens');
        clearTokens();
        pendingAuthRef.current = null;
        updateFirebaseUser(null);
        updateBackendUser(null);
        updateProfile(null);
        localStorage.removeItem('client_userProfile');
        localStorage.removeItem('client_userRole');
        auth.signOut().catch(() => {});

        resetFormState();
        onClose();
      };
      
      setOnCloseWarningConfirm(() => confirmLogoutCallback);
      openCloseWarning();
      return; // Don't close yet
    }

    if (activeLoginOptions?.fromRegistration) {
      openCloseWarning({
        title: 'Unsaved Registration',
        message: 'You have unsaved registration data. If you close without completing signup, your data will be lost. Are you sure you want to close?',
        cancelText: 'Continue Signup',
        confirmText: 'Close',
        onConfirm: () => {
          resetFormState();
          onClose();
        },
      });
      return;
    }
    
    logger.log('LoginModal', 'Calling resetFormState...');
    resetFormState();
    logger.log('LoginModal', 'After resetFormState, calling onClose...');
    logger.log('LoginModal', 'onClose callback', { type: typeof onClose });
    onClose();
    logger.log('LoginModal', 'onClose() executed');
  };
  if (!isOpen) return null;

  const authTitle = (
    <div className="flex flex-col">
      <span>{getModalTitle()}</span>
      <span className="text-sm font-normal text-white/80">
        {userType === 'parent' ? 'Parent/Student Account' : 'Tutor Account'}
      </span>
    </div>
  );

  const authForm = (
    <form onSubmit={(e) => {
      e.preventDefault();
      // Trigger the appropriate submit handler based on current mode/view
      if (modalView === 'login') {
        handleEmailLogin();
      }
    }} className="space-y-6">
        {/* User Type Toggle — hidden when role is locked by a pending registration or in forgot password flow */}
        {isRoleLocked || modalView.startsWith('forgot_password') ? (
          null
        ) : (
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => {
                setUserType('parent');
                resetFormState();
              }}
              disabled={loading}
              className={`flex-1 py-2 px-4 rounded-md transition-colors font-medium disabled:opacity-50 ${userType === 'parent' ? 'bg-accent text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Parent/Student
            </button>
            <button
              type="button"
              onClick={() => {
                setUserType('tutor');
                resetFormState();
              }}
              disabled={loading}
              className={`flex-1 py-2 px-4 rounded-md transition-colors font-medium disabled:opacity-50 ${userType === 'tutor' ? 'bg-accent text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Tutor
            </button>
          </div>
        )}

        {/* Render appropriate form */}
        {renderContent()}

        {/* Auth Mode Toggle Link - only show on login or signup method pages */}
        {(modalView === 'login' || modalView === 'signup_method') && (
          <div className="text-center text-sm text-gray-600">
            {modalView === 'login' ? (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('signup');
                    setModalView('signup_method');
                  }}
                  disabled={loading}
                  className="text-primary hover:underline font-semibold disabled:opacity-50"
                >
                  Sign Up
                </button>
              </>
            ) : modalView === 'signup_method' ? (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('login');
                    setModalView('login');
                    resetFormState();
                  }}
                  disabled={loading}
                  className="text-primary hover:underline font-semibold disabled:opacity-50"
                >
                  Log In
                </button>
              </>
            ) : null}
          </div>
        )}

        {/* Terms and Privacy - only on signup pages */}
        {modalView.startsWith('signup') && (
          <p className="text-xs text-gray-500 text-center">
            By signing up, you agree to our{' '}
            <a href="/terms-tutors" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Privacy Policy
            </a>
          </p>
        )}
    </form>
  );

  if (presentation === 'page') {
    return (
      <section
        aria-labelledby="authentication-page-title"
        className="mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-[0_24px_70px_-36px_rgba(122,84,31,0.35)]"
      >
        <header className="flex items-center gap-3 bg-primary px-5 py-5 sm:px-6">
          {showPageBackButton && (
            <button
              type="button"
              onClick={activeLoginOptions?.fromRegistration && onBackToRegistration ? returnToRegistration : handleClose}
              aria-label={activeLoginOptions?.fromRegistration && onBackToRegistration ? 'Back to registration form' : 'Back to registration'}
              className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-xl text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          <div>
            <h1 id="authentication-page-title" className="text-xl font-semibold text-white sm:text-2xl">
              {getModalTitle()}
            </h1>
            <p className="text-sm text-white/80">
              {userType === 'parent' ? 'Parent/Student Account' : 'Tutor Account'}
            </p>
          </div>
        </header>
        <div className="px-5 py-6 sm:px-6 sm:py-8">{authForm}</div>
      </section>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!activeLoginOptions?.fromRegistration}
      title={authTitle}
      maxWidth="max-w-md"
    >
      {authForm}
    </Modal>
  );
};

export default LoginModal;
